// Ekran 04'ün liste sayfası ile tam sayfa analiz sekmesinin paylaştığı küçük
// parçalar. Ayrı dosyada duruyorlar çünkü liste sayfası sekmeyi import ediyor;
// tersi de olsaydı iki modül birbirini çağıran bir döngü kurardı.

import { tr } from '../../lib/trFormat'
import type { CariOzet } from './api'

export type RolAnahtar = 'rolAtik' | 'rolTedarikci' | 'rolNakliyeci'

/**
 * Referans `ROL` sözlüğü + `swRow()` açıklamaları.
 *
 * `not` inceleme panelinin kısa açıklaması, `notUzun` tam sayfadaki "Rol
 * Yönetimi" kartınınki — referans ikisini farklı yazıyor
 * (ref_files/btys-erp-04-cariler.html:520 ve :588).
 */
export const ROLLER: {
  anahtar: RolAnahtar
  harf: string
  etiket: string
  not: string
  notUzun: string
}[] = [
  // Etiketler referansın `ROL` sözlüğünden (ref:394) birebir: segment, KPI ve
  // rozet başlığı aynı sözcüğü kullanmalı, yoksa ekran kendi içinde çelişir.
  { anahtar: 'rolAtik', harf: 'A', etiket: 'Atık Sağlayıcı', not: 'hammadde kaynağı', notUzun: 'hammadde kaynağı tesis' },
  { anahtar: 'rolTedarikci', harf: 'T', etiket: 'Tedarikçi', not: 'sarf / malzeme', notUzun: 'sarf / malzeme / yakıt' },
  { anahtar: 'rolNakliyeci', harf: 'N', etiket: 'Nakliyeci', not: 'taşıma', notUzun: 'taşıma operasyonu' },
]

/** Referans `rolB()` — açık rollerin harf rozetleri. */
export function RolRozetleri({ c }: { c: CariOzet }) {
  return (
    <>
      {ROLLER.filter((r) => c[r.anahtar]).map((r) => (
        <span className="rolb" key={r.harf} title={r.etiket}>
          {r.harf}
        </span>
      ))}
    </>
  )
}

/**
 * Referans `swRow()` (ref:509-510) — rol anahtarı satırı.
 *
 * `<button>` çünkü referansta da buton: tık hedefi 30px'lik anahtar değil satırın
 * tamamı, anahtar `.swrow .sw{margin-left:auto}` ile sağa yaslanıyor. Buton
 * olması klavye erişimini de bedavaya getiriyor (elle Space/Enter işlemesi yok).
 *
 * `uzun` tam sayfadaki "Rol Yönetimi" kartının uzun açıklamalarını basar
 * (ref:588); inceleme panelinde kısa olanlar geçerli (ref:520).
 */
export function RolAnahtarlari({
  c,
  uzun = false,
  kilitli = false,
  kilitNedeni,
  degistir,
}: {
  c: CariOzet
  uzun?: boolean
  kilitli?: boolean
  kilitNedeni?: string
  degistir: (anahtar: RolAnahtar) => void
}) {
  return (
    <>
      {ROLLER.map((r) => (
        <button
          key={r.anahtar}
          className="swrow"
          type="button"
          role="switch"
          aria-checked={c[r.anahtar]}
          disabled={kilitli}
          title={kilitNedeni}
          onClick={() => degistir(r.anahtar)}
        >
          <span>{r.etiket}</span>
          <small>{uzun ? r.notUzun : r.not}</small>
          <span className={c[r.anahtar] ? 'sw on' : 'sw'} />
        </button>
      ))}
    </>
  )
}

/**
 * Referans `sapCls()` — |sapma| > 10 kırmızı, > 5 amber.
 *
 * `.sapma-r/.sapma-a` referansın kendi CSS'inde tanımsız (kendi kusuru, tıpkı
 * `.inst-note` gibi); §3.2 gereği sınıf adı aynen korunuyor, yeni kural yazılmıyor.
 */
export const sapmaSinifi = (d: number | null | undefined) =>
  d === null || d === undefined ? '' : Math.abs(d) > 10 ? 'sapma-r' : Math.abs(d) > 5 ? 'sapma-a' : ''

/**
 * Aynı eşiklerin koyu/KPI zeminli karşılığı: `.inst-v` ve `.k-v` için
 * design-system.css'te tanımlı olan `.red` / `.amber` sınıfları.
 */
export const sapmaVurgusu = (d: number | null | undefined) =>
  d === null || d === undefined ? '' : Math.abs(d) > 10 ? 'red' : Math.abs(d) > 5 ? 'amber' : ''

export const sapmaMetni = (d: number | null | undefined) =>
  d === null || d === undefined ? '—' : `${d > 0 ? '+' : ''}${tr(d, 1)}`
